/**
 * Bridge between the (non-React) axios response interceptor and Auth0.
 *
 * When the backend rejects a request with 401, the Auth0 access token is
 * expired/invalid. The interceptor lives outside React and can't call the
 * `useAuth0()` hooks, so a top-level auth component registers a handler here
 * (via `setUnauthorizedHandler`) that performs the actual re-authentication.
 *
 * Without this, an expired-but-cached session (introduced by `useRefreshTokens`
 * + `cacheLocation: 'localstorage'`) lets the app proceed and surface a generic
 * "Error Creating Your Application (#00013)" instead of re-authenticating.
 */
type UnauthorizedHandler = () => void;

let handler: UnauthorizedHandler | null = null;
let handling = false;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  handler = fn;
}

/**
 * Trigger re-authentication after a 401. Idempotent: many in-flight requests
 * can 401 at once, but only the first drives the redirect so we don't fire
 * multiple logins. The flag resets naturally on the next full page load.
 */
export function handleUnauthorized(): void {
  if (handling) return;
  handling = true;
  if (handler) {
    handler();
  } else if (typeof window !== 'undefined') {
    // No handler registered yet — hard-reset to the first page as a fallback.
    window.location.assign('/');
  }
}
